/* eslint-disable @typescript-eslint/no-shadow */
/*

Create objects to use in a slideshow for showing spreads

------------
INIT SPREADS:
------------
╔═════════════════════════╗
║ Core: Broadcaster       ║
╚═══╤═════════════════════╝
	│ 1) onMessage (new spreads)
    ↓
╔═══════════════════════════════════════════════════════════════════════════╗
║          Slideshow-Previews-Manager                                       ║
╚═════════════════════════════════════════════════╤═════════════════════════╝
                                                  ↓
╔══════════════════════════════╗      ╔═════════════════════════════════════╗
║ Slideshow                    ║      ║ Spread-slideshow                    ║
║                              ║      ║                                     ║
║                              ║      ║ 2) create items                     ║
║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
║ 3) setItems                ←─║──────║─┤                             │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
║ 4) onViewChanged           ──║──────║─┼→ 5) updatePreviewAsync      │ │   ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
║                              ║      ║ └─────────────────────────────┘ │ ┐ ║      └───────────────────────┘ │      └───────────────────────┘ │
╚══════════════════════════════╝      ║   └─────────────────────────────┘ │ ║        └───────────────────────┘        └───────────────────────┘
                                      ║     └─────────────────────────────┘ ║
                                      ╚═════════════════════════════════════╝

---------------
RENDER PREVIEW:
---------------
╔═════════════════════════╗
║ Core: Broadcaster       ║
╚═══╤═════════════════════╝
	│ 1) onMessage (Spread updated)
    ↓
╔═══════════════════════════════════════════════════════════════════════════╗
║          Slideshow-Previews-Manager                                       ║
╚═════════════════════════════════════════════════╤═════════════════════════╝
                                                  ↓
╔══════════════════════════════╗      ╔═════════════════════════════════════╗
║ Slideshow                    ║      ║ Spread-slideshow                    ║
║                              ║      ║ ┌─────────────────────────────┐     ║    ┌───────────────────────┐        ┌───────────────────────┐
║ 3) setItemSize             ←─║──────║─┼─ 2) updatePreviewAsync      │ ┐ ──║───→│ renderers             │ ┐ ────→│ DOM elements          │ ┐
║ 4) onItemTransformChanged  ──║──────║─┼→ 5) applyTransform          │ │   ║    └───────────────────────┘ │ ┐    └───────────────────────┘ │ ┐
║                              ║      ║ └─────────────────────────────┘ │ ┐ ║      └───────────────────────┘ │      └───────────────────────┘ │
╚══════════════════════════════╝      ║   └─────────────────────────────┘ │ ║        └───────────────────────┘        └───────────────────────┘
                                      ║     └─────────────────────────────┘ ║
                                      ╚═════════════════════════════════════╝

*/

import "requestidlecallback-polyfill";
import { ISlideShow, ISlideShowItemSceneGraph } from "../slideshow/slideshow-js";
import { createAdornerRenderer, IAdornerRendererDeps } from "./renderers/adorner-renderer";
import {
	createPreviewRenderer,
	IPreviewRenderer,
	IPreviewRendererDeps,
	IPreviewSceneGraph,
} from "./renderers/preview-renderer";
import { createSpreadRenderer, ISpreadRendererDeps } from "./renderers/spread-renderer";

interface ISpreadSlideshowItemOptions {
	renderingLoadingText: string;
	showDebugLayers?: boolean;
	showAdorners?: boolean;
	renderEffects?: boolean;
	useSvgLayer?: boolean;
	maxRenderSizeInPx?: number;
}

interface ISpreadSlideshowItemDeps
	extends Omit<IPreviewRendererDeps, "spreadRenderer" | "adornerRenderer">,
		ISpreadRendererDeps,
		IAdornerRendererDeps {
	logger: eXpress.ILogger;
	platform: eXpress.platforms.IPlatform;
	client: Pick<eXpress.core.IClient, "logErrorAsync" | "getAbsoluteAdornerResourceUrl">;
}

/**
 * The preview render is a combination of diffrernt renderers
 * - Spread, Adorner, ZoneSelectionBox, Grid, ...
 */
export interface ISpreadSlideShowItem {
	readonly spread: eXpress.core.Spread;

	readonly isLoading: boolean;

	/** Slideshow wants use to update the preview with this transform */
	applyTransform(transforms: ISlideShowItemSceneGraph): void;

	/** Show spread preview */
	showPreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone): Promise<void>;

	/** Load spread resources (Adorner, images, fonts, Backgrounds, ...) and update DOM when ready */
	updatePreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone): Promise<void>;

	/** Remove the spread preview from the DOM */
	hidePreview(): void;

	/** Set the active state of the preview */
	setActive(isActive: boolean): void;

	/** Can return undefined if preview is not available (disposed) */
	readonly sceneGraph: IPreviewSceneGraph | undefined;

	/** Get the zones clicked */
	getZonesAtPoint(pointInPreviewSpace: eXpress.math.Point): eXpress.core.IZone[];

	dispose(): void;
}

export function createSpreadSlideShowItem(
	slideShow: ISlideShow<ISpreadSlideShowItem>,
	spread: eXpress.core.Spread,
	options: ISpreadSlideshowItemOptions,
	deps: ISpreadSlideshowItemDeps,
): ISpreadSlideShowItem {
	let previewRenderer: IPreviewRenderer | undefined;
	let isDisposed = false;

	/**
	 * Get or create a preview render item
	 * A Preview renderer is a combination of a spread a adorner rendering
	 */
	function getPreviewRenderer() {
		if (isDisposed) return undefined;
		if (previewRenderer) return previewRenderer;

		// Create a new PreviewRenderer
		const { showAdorners = true } = options;

		previewRenderer = createPreviewRenderer(spread, {
			spreadRenderer: createSpreadRenderer(options, deps),
			adornerRenderer: showAdorners ? createAdornerRenderer(deps) : undefined,
			...deps,
		});

		slideShow.el.appendChild(previewRenderer.el);

		return previewRenderer;
	}

	/** Transform on the spread representation */
	function applyTransform(transforms: ISlideShowItemSceneGraph) {
		if (!previewRenderer) return; // Only apply Transform when renderer is available
		const { slideshow, slideshowItem } = transforms;
		const transform = slideshowItem.getTransformTo(slideshow);
		previewRenderer.applyTransform(transform);
	}

	/** Get the zones at a point */
	function getZonesAtPoint(pointInPreviewSpace: eXpress.math.Point) {
		const previewRenderer = getPreviewRenderer();
		return previewRenderer?.getZonesAtPoint(pointInPreviewSpace) ?? [];
	}

	let requestedPreviewId = -1;
	let renderedPreviewId = -2;

	async function updatePreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone) {
		const requestedId = ++requestedPreviewId;
		const previewRenderer = getPreviewRenderer();
		try {
			// Only show newer preview requests of this spread
			if (renderedPreviewId >= requestedPreviewId) return;
			await previewRenderer?.updatePreviewAsync(previewMode, topZone);
		} finally {
			renderedPreviewId = requestedId;
		}
	}

	async function showPreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone) {
		// For now do the same as before
		await updatePreviewAsync(previewMode, topZone);

		// Perform correct transform for newly added items
		const previewRenderer = getPreviewRenderer();
		const itemInfo = slideShow.getItemInfo(item);
		if (previewRenderer && itemInfo) {
			const { slideshow, slideshowItem } = itemInfo.transforms;
			const transform = slideshowItem.getTransformTo(slideshow);
			previewRenderer.applyTransform(transform);
		}
	}

	function hidePreview() {
		// We could later consider doing some tricks here.
		// If we still want to keep the preview visible but go in a suspended mode,
		// we could convert the preview to an image so we can free up canvases
		if (previewRenderer) {
			previewRenderer.el.remove(); // Remove from DOM
			previewRenderer.dispose();
			previewRenderer = undefined;
		}
	}

	function setActive(isActive: boolean) {
		previewRenderer?.setActive?.(isActive);
	}

	function dispose() {
		hidePreview();
		if (previewRenderer) {
			previewRenderer.el.remove(); // Remove from DOM
			previewRenderer.dispose();
			previewRenderer = undefined;
		}
		isDisposed = true;
	}

	const item: ISpreadSlideShowItem = {
		spread,

		showPreviewAsync,
		updatePreviewAsync,
		hidePreview,
		setActive,

		get isLoading() {
			return renderedPreviewId !== requestedPreviewId;
		},

		get sceneGraph() {
			return getPreviewRenderer()?.sceneGraph;
		},

		applyTransform,

		getZonesAtPoint,

		dispose,
	};

	return item;
}
